import { useState } from 'react';

import type { MemoryDto } from '../../api/types';

const COLLAPSE_THRESHOLD = 3;

function itemText(item: unknown): string {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    const text = obj.text ?? obj.value ?? obj.label;
    if (typeof text === 'string') return text;
  }
  return JSON.stringify(item);
}

function toItems(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(itemText);
}

function MemorySection({ title, items }: { title: string; items: string[] }) {
  const [expanded, setExpanded] = useState(false);
  if (items.length === 0) {
    return (
      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">{title}</div>
        <span className="text-xs text-muted-fg">Нет данных</span>
      </div>
    );
  }
  const collapsible = items.length > COLLAPSE_THRESHOLD;
  const visible = collapsible && !expanded ? items.slice(0, COLLAPSE_THRESHOLD) : items;
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-muted-fg">{title}</div>
      <ul className="list-inside list-disc space-y-0.5 text-xs">
        {visible.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
      {collapsible && (
        <button
          type="button"
          className="mt-1 text-xs text-primary hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Свернуть' : `Показать ещё ${items.length - COLLAPSE_THRESHOLD}`}
        </button>
      )}
    </div>
  );
}

/** Contact memory: summary plus facts/objections/agreements lists, each collapsible past a few items. */
export function MemoryList({ memory }: { memory: MemoryDto | null }) {
  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Память: сводка</div>
        <p className="text-xs text-muted-fg">{memory?.summary ?? 'Нет сводки'}</p>
      </div>
      <MemorySection title="Факты" items={toItems(memory?.facts)} />
      <MemorySection title="Возражения" items={toItems(memory?.objections)} />
      <MemorySection title="Договорённости" items={toItems(memory?.agreements)} />
    </div>
  );
}
